import React from 'react';
import styles from './FiltersSidebar.module.css';
import {
  Accordion, AccordionSummary, AccordionDetails, Switch, FormControlLabel,
  Slider, Checkbox, FormGroup, FormControl, InputLabel, MenuItem, Select, TextField, Chip
} from '@mui/material';
import { ArrowDownIcon } from '../../../../../utils/Icons';

// Fallbacks (used when dynamic lists are empty)
const FALLBACK_BRANDS = ['Philips', 'OSRAM', 'GE', 'Cree'];
const FALLBACK_CATEGORIES = ['Floor lights', 'Ceiling lights', 'Pendant lights', 'Table lamps', 'Wall lights', 'Spotlights', 'Chandeliers'];

const whiteLightRange = ['Warm White (2200K–3000K)', 'Neutral White (3500K–4100K)', 'Cool White (5000K–6500K)', 'Daylight (6500K+)'];

const INSTALLATION = [
  { key: 'Ceiling', img: 'https://dummyimage.com/28x28/ffffff/d9d9d9.png&text=C' },
  { key: 'Surface', img: 'https://dummyimage.com/28x28/ffffff/d9d9d9.png&text=S' },
  { key: 'Suspended', img: 'https://dummyimage.com/28x28/ffffff/d9d9d9.png&text=Su' },
  { key: 'Standing', img: 'https://dummyimage.com/28x28/ffffff/d9d9d9.png&text=St' },
];

const CONTROLS = [
  { key: 'DALI', img: 'https://dummyimage.com/28x28/ffffff/d9d9d9.png&text=DA' },
  { key: 'DMX', img: 'https://dummyimage.com/28x28/ffffff/d9d9d9.png&text=DX' },
  { key: 'Phase Dimmable', img: 'https://dummyimage.com/28x28/ffffff/d9d9d9.png&text=PD' },
];

export default function FiltersSidebar({
  filters, setFilters, onClearAll,

  // dynamic options per source
  dynamicBrandsLocal = [],
  dynamicCategoriesLocal = [],
  dynamicColorsLocal = [],
  dynamicBrandsInternet = [],
  dynamicCategoriesInternet = [],
  dynamicColorsInternet = [],

  dynamicPriceMin = 0,
  dynamicPriceMax = 3000,
}) {
  const [isFilterExpanded, setIsFilterExpanded] = React.useState(true);
  // ---------- safe accessors ----------
  const arr = (v) => (Array.isArray(v) ? v : []);
  const bool = (v) => !!v;
  const num2 = (v, fallbackLo, fallbackHi) =>
    Array.isArray(v) && v.length === 2 ? v : [fallbackLo, fallbackHi];

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const toggleArray = (key, v) => {
    const cur = arr(filters[key]);
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    set({ [key]: next });
  };

  const price = num2(filters.price, dynamicPriceMin, dynamicPriceMax);
  const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);
  const onPriceMin = (v) => {
    const min = clamp(Number(v) || 0, dynamicPriceMin, price[1]);
    set({ price: [min, price[1]] });
  };
  const onPriceMax = (v) => {
    const max = clamp(Number(v) || 0, price[0], dynamicPriceMax);
    set({ price: [price[0], max] });
  };

  const heads = num2(filters.heads, 0, 200);
  const onHeadsMin = (v) => set({ heads: [clamp(Number(v) || 0, 0, heads[1]), heads[1]] });
  const onHeadsMax = (v) => set({ heads: [heads[0], clamp(Number(v) || 0, heads[0], 200)] });

  const lumens = num2(filters.lumens, 0, 30000);
  const onLumensMin = (v) => set({ lumens: [clamp(Number(v) || 0, 0, lumens[1]), lumens[1]] });
  const onLumensMax = (v) => set({ lumens: [lumens[0], clamp(Number(v) || 0, lumens[0], 30000)] });

  // Source selection
  const sources = arr(filters.sources);
  const hasLocal = sources.includes('local');
  const hasInternet = sources.includes('internet');
  const internetOnly = hasInternet && !hasLocal;

  // Choose dynamic lists based on current source selection.
  // If both selected, union them (simple + predictable).
  const union = (a, b) => Array.from(new Set([...(a || []), ...(b || [])]));
  const brandOptions = (internetOnly
    ? (dynamicBrandsInternet.length ? dynamicBrandsInternet : FALLBACK_BRANDS)
    : hasLocal && hasInternet
      ? union(dynamicBrandsLocal, dynamicBrandsInternet)
      : (dynamicBrandsLocal.length ? dynamicBrandsLocal : FALLBACK_BRANDS)
  ).slice(0, 200);

  const categoryOptions = (internetOnly
    ? (dynamicCategoriesInternet.length ? dynamicCategoriesInternet : FALLBACK_CATEGORIES)
    : hasLocal && hasInternet
      ? union(dynamicCategoriesLocal, dynamicCategoriesInternet)
      : (dynamicCategoriesLocal.length ? dynamicCategoriesLocal : FALLBACK_CATEGORIES)
  ).slice(0, 200);

  const colorDots = (internetOnly
    ? (dynamicColorsInternet.length ? dynamicColorsInternet : ['#000000', '#ffffff', '#f59e0b', '#111827', '#1f2937', '#0ea5e9'])
    : hasLocal && hasInternet
      ? union(dynamicColorsLocal, dynamicColorsInternet)
      : (dynamicColorsLocal.length ? dynamicColorsLocal : ['#000000', '#ffffff', '#f59e0b', '#111827', '#1f2937', '#0ea5e9'])
  ).slice(0, 12);

  return (
    <div className={styles.wrap}>
      <div className={styles.topRow}>
        <h4 className={styles.title} onClick={() => { setIsFilterExpanded(!isFilterExpanded) }} >Filters</h4>
        <div className='flexAlign10' >
          <button className={styles.clear} onClick={onClearAll}>Clear All</button>
          <div className={styles.filterToggle} >
            <ArrowDownIcon height={12} fill='#878787' className={`${styles.chev} ${isFilterExpanded ? styles.open : ""}`} onClick={() => { setIsFilterExpanded(!isFilterExpanded) }} />
          </div>
        </div>
      </div>
      {isFilterExpanded &&
        <>
          {/* Source (NEW) */}
          <Accordion defaultExpanded disableGutters className={styles.acc}>
            <AccordionSummary expandIcon={<ArrowDownIcon height={12} fill='#878787' />} className={styles.accSummary}>
              <span className={styles.accTitle}>Source</span>
            </AccordionSummary>
            <AccordionDetails className={styles.accDetails}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasLocal}
                      onChange={() => toggleArray('sources', 'local')}
                    />
                  }
                  label="Registered manufacturers (our site)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasInternet}
                      onChange={() => toggleArray('sources', 'internet')}
                    />
                  }
                  label="Global internet manufacturers"
                />
              </FormGroup>
            </AccordionDetails>
          </Accordion>
          {/* Category */}
          <Accordion defaultExpanded disableGutters className={styles.acc}>
            <AccordionSummary expandIcon={<ArrowDownIcon height={12} fill='#878787' />} className={styles.accSummary}>
              <span className={styles.accTitle}>Product Category</span>
            </AccordionSummary>
            <AccordionDetails className={styles.accDetails}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={filters.category || ''}
                  onChange={(e) => set({ category: e.target.value })}
                  MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
                >
                  <MenuItem sx={{ fontSize: 13, fontFamily: 'Manrope' }} value="">All</MenuItem>
                  {categoryOptions.map((c) => <MenuItem sx={{ fontSize: 13, fontFamily: 'Manrope' }} key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </AccordionDetails>
          </Accordion>
          {/* Brand */}
          <Accordion defaultExpanded disableGutters className={styles.acc}>
            <AccordionSummary expandIcon={<ArrowDownIcon height={12} fill='#878787' />} className={styles.accSummary}>
              <span className={styles.accTitle}>Brand</span>
            </AccordionSummary>
            <AccordionDetails className={styles.accDetails}>
              <FormControl fullWidth size="small">
                <InputLabel>Brand</InputLabel>
                <Select
                  label="Brand"
                  value={filters.brand || ''}
                  onChange={(e) => set({ brand: e.target.value })}
                  MenuProps={{ PaperProps: { style: { maxHeight: 280 } } }}
                >
                  <MenuItem sx={{ fontSize: 13, fontFamily: 'Manrope' }} value="">All</MenuItem>
                  {brandOptions.map((b) => <MenuItem sx={{ fontSize: 13, fontFamily: 'Manrope' }} key={b} value={b}>{b}</MenuItem>)}
                </Select>
              </FormControl>
            </AccordionDetails>
          </Accordion>
          {/* Price */}
          <Accordion defaultExpanded disableGutters className={styles.acc}>
            <AccordionSummary expandIcon={<ArrowDownIcon height={12} fill='#878787' />} className={styles.accSummary}>
              <span className={styles.accTitle}>Price</span>
            </AccordionSummary>
            <AccordionDetails className={styles.accDetails}>
              <div className={styles.priceRow}>
                <div className={styles.priceInputs}>
                  <TextField
                    type="number"
                    value={price[0]}
                    onChange={(e) => onPriceMin(e.target.value)}
                    inputProps={{ min: dynamicPriceMin, max: price[1], step: 1 }}
                    className={styles.numField}
                    label="$ Min"
                  />
                  <span className={styles.sep}>–</span>
                  <TextField
                    type="number"
                    value={price[1]}
                    onChange={(e) => onPriceMax(e.target.value)}
                    inputProps={{ min: price[0], max: dynamicPriceMax, step: 1 }}
                    className={styles.numField}
                    label="$ Max"
                  />
                </div>
                <Slider
                  value={price}
                  onChange={(_, v) => set({ price: v })}
                  min={dynamicPriceMin}
                  max={dynamicPriceMax}
                  step={1}
                />
              </div>

              <div className={styles.toggles}>
                <FormControlLabel
                  control={<Switch checked={bool(filters.fast)} onChange={(e) => set({ fast: e.target.checked })} />}
                  label="Fast Shipping"
                />
                <FormControlLabel
                  control={<Switch checked={bool(filters.available)} onChange={(e) => set({ available: e.target.checked })} />}
                  label="Only Available Items"
                />
              </div>
            </AccordionDetails>
          </Accordion>
          {/* Color */}
          <Accordion defaultExpanded disableGutters className={styles.acc}>
            <AccordionSummary expandIcon={<ArrowDownIcon height={12} fill='#878787' />} className={styles.accSummary}>
              <span className={styles.accTitle}>Color</span>
            </AccordionSummary>
            <AccordionDetails className={styles.accDetails}>
              <div className={styles.colorRow}>
                {colorDots.map((c) => (
                  <button
                    key={c}
                    className={`${styles.dot} ${arr(filters.colors).includes(c) ? styles.dotActive : ''}`}
                    style={{ backgroundColor: c }}
                    onClick={() => toggleArray('colors', c)}
                    title={c}
                  />
                ))}
              </div>
            </AccordionDetails>
          </Accordion>
          {/* ── The sections below are local-only; hide them when Internet-only is selected ── */}
          {!internetOnly && (
            <>
              {/* Light Color */}
              <Accordion defaultExpanded disableGutters className={styles.acc}>
                <AccordionSummary expandIcon={<ArrowDownIcon height={12} fill='#878787' />} className={styles.accSummary}>
                  <span className={styles.accTitle}>Light Color</span>
                </AccordionSummary>
                <AccordionDetails className={styles.accDetails}>
                  <FormControl fullWidth size="small">
                    <InputLabel>White Light Range</InputLabel>
                    <Select
                      label="White Light Range"
                      value={filters?.whiteLightRange || ''}
                      onChange={(e) => set({ whiteLightRange: e.target.value })}
                      MenuProps={{ PaperProps: { style: { maxHeight: 240 } } }}
                    >
                      <MenuItem sx={{ fontSize: 13, fontFamily: 'Manrope' }} value="">All</MenuItem>
                      {whiteLightRange.map((c) => <MenuItem sx={{ fontSize: 13, fontFamily: 'Manrope' }} key={c} value={c}>{c}</MenuItem>)}
                    </Select>
                  </FormControl>

                  <div className={styles.subLabel} style={{ marginTop: '10px' }} >Adjustable / Smart Options</div>

                  <FormGroup className={styles.ckRow}>
                    <FormControlLabel control={<Checkbox checked={bool(filters.rgbw)} onChange={(e) => set({ rgbw: e.target.checked })} />} label="RGBW" />
                    <FormControlLabel control={<Checkbox checked={bool(filters.tunableWhite)} onChange={(e) => set({ tunableWhite: e.target.checked })} />} label="Tunable White" />
                  </FormGroup>
                </AccordionDetails>
              </Accordion>

              {/* Installation */}
              <Accordion defaultExpanded disableGutters className={styles.acc}>
                <AccordionSummary expandIcon={<ArrowDownIcon height={12} fill='#878787' />} className={styles.accSummary}>
                  <span className={styles.accTitle}>Installation</span>
                </AccordionSummary>
                <AccordionDetails className={styles.accDetails}>
                  <div className={styles.boxGrid}>
                    {INSTALLATION.map((m) => {
                      const on = arr(filters.mountings).includes(m.key);
                      return (
                        <button
                          key={m.key}
                          className={`${styles.selBox} ${on ? styles.selBoxOn : ''}`}
                          onClick={() => toggleArray('mountings', m.key)}
                        >
                          <img className={styles.boxImg} src={m.img} alt={m.key} />
                          <span className={styles.boxLabel}>{m.key}</span>
                        </button>
                      );
                    })}
                  </div>
                </AccordionDetails>
              </Accordion>

              {/* Shape */}
              <Accordion defaultExpanded disableGutters className={styles.acc}>
                <AccordionSummary expandIcon={<ArrowDownIcon height={12} fill='#878787' />} className={styles.accSummary}>
                  <span className={styles.accTitle}>Shape</span>
                </AccordionSummary>
                <AccordionDetails className={styles.accDetails}>
                  <FormGroup className={styles.ckRow}>
                    <FormControlLabel
                      control={<Checkbox checked={arr(filters.shapes).includes('Round')} onChange={() => toggleArray('shapes', 'Round')} />}
                      label="Round"
                    />
                    <FormControlLabel
                      control={<Checkbox checked={arr(filters.shapes).includes('Square')} onChange={() => toggleArray('shapes', 'Square')} />}
                      label="Square"
                    />
                  </FormGroup>

                  <FormGroup className={styles.ckRow}>
                    <FormControlLabel control={<Checkbox checked={bool(filters.adjustable)} onChange={(e) => set({ adjustable: e.target.checked })} />} label="Adjustable" />
                    <FormControlLabel control={<Checkbox checked={bool(filters.wallMounted)} onChange={(e) => set({ wallMounted: e.target.checked })} />} label="Wall mountable" />
                    <FormControlLabel control={<Checkbox checked={bool(filters.base)} onChange={(e) => set({ base: e.target.checked })} />} label="Base included" />
                  </FormGroup>
                </AccordionDetails>
              </Accordion>

              {/* Control */}
              <Accordion defaultExpanded disableGutters className={styles.acc}>
                <AccordionSummary expandIcon={<ArrowDownIcon height={12} fill='#878787' />} className={styles.accSummary}>
                  <span className={styles.accTitle}>Control</span>
                </AccordionSummary>
                <AccordionDetails className={styles.accDetails}>
                  <div className={styles.boxGrid}>
                    {CONTROLS.map((c) => {
                      const on = arr(filters.controls).includes(c.key);
                      return (
                        <button
                          key={c.key}
                          className={`${styles.selBox} ${on ? styles.selBoxOn : ''}`}
                          onClick={() => toggleArray('controls', c.key)}
                        >
                          <img className={styles.boxImg} src={c.img} alt={c.key} />
                          <span className={styles.boxLabel}>{c.key}</span>
                        </button>
                      );
                    })}
                  </div>
                </AccordionDetails>
              </Accordion>

              {/* POWER */}
              <Accordion defaultExpanded disableGutters className={styles.acc}>
                <AccordionSummary expandIcon={<ArrowDownIcon height={12} fill='#878787' />} className={styles.accSummary}>
                  <span className={styles.accTitle}>Power</span>
                </AccordionSummary>
                <AccordionDetails className={styles.accDetails}>
                  <div className={styles.subLabel}>Power (W)</div>
                  <Slider value={num2(filters.power, 0, 300)} onChange={(_, v) => set({ power: v })} min={0} max={300} step={1} />

                  <div className={styles.dualRow}>
                    <div className={styles.numGroup}>
                      <div className={styles.subLabel}>Count / Heads</div>
                      <div className={styles.dualInputs}>
                        <TextField type="number" value={heads[0]} onChange={(e) => onHeadsMin(e.target.value)} inputProps={{ min: 0, max: heads[1] }} className={styles.numField} label="Min" />
                        <span className={styles.sep}>–</span>
                        <TextField type="number" value={heads[1]} onChange={(e) => onHeadsMax(e.target.value)} inputProps={{ min: heads[0], max: 200 }} className={styles.numField} label="Max" />
                      </div>
                    </div>

                    <div className={styles.numGroup}>
                      <div className={styles.subLabel}>Luminous intensity (lm)</div>
                      <div className={styles.dualInputs}>
                        <TextField type="number" value={lumens[0]} onChange={(e) => onLumensMin(e.target.value)} inputProps={{ min: 0, max: lumens[1], step: 50 }} className={styles.numField} label="Min" />
                        <span className={styles.sep}>–</span>
                        <TextField type="number" value={lumens[1]} onChange={(e) => onLumensMax(e.target.value)} inputProps={{ min: lumens[0], max: 30000, step: 50 }} className={styles.numField} label="Max" />
                      </div>
                    </div>
                  </div>

                  <div className={styles.subLabel} style={{ marginTop: 8 }}>Luminous efficiency</div>
                  <div className={styles.chipRow}>
                    {['gt100', 'gt120'].map((k) => (
                      <Chip
                        key={k}
                        label={k === 'gt100' ? '>100 lm/W' : '>120 lm/W'}
                        variant={arr(filters.efficiency).includes(k) ? 'filled' : 'outlined'}
                        color={arr(filters.efficiency).includes(k) ? 'primary' : 'default'}
                        onClick={() => toggleArray('efficiency', k)}
                        size="small"
                      />
                    ))}
                  </div>
                </AccordionDetails>
              </Accordion>

              {/* Voltage */}
              <Accordion defaultExpanded disableGutters className={styles.acc}>
                <AccordionSummary expandIcon={<ArrowDownIcon height={12} fill='#878787' />} className={styles.accSummary}>
                  <span className={styles.accTitle}>Voltage</span>
                </AccordionSummary>
                <AccordionDetails className={styles.accDetails}>
                  <div className={styles.chipRow}>
                    {['110V', '220V'].map((v) => (
                      <Chip
                        key={v}
                        label={v}
                        variant={arr(filters.voltage).includes(v) ? 'filled' : 'outlined'}
                        color={arr(filters.voltage).includes(v) ? 'primary' : 'default'}
                        onClick={() => toggleArray('voltage', v)}
                        size="small"
                      />
                    ))}
                  </div>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </>
      }
    </div>
  );
}
